
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Provider, Validate } from '@/types';
import { MEMBERSHIP_PROVIDERS } from '@/utils/constants';
import { buildMemberInfoFromProvider, getMembershipInputSettings } from '@/utils/membership-helpers';
import { formatPhoneNumber } from '@/utils/format';
import ExpansionPanel from '@/components/shared/suites/panels/ExpansionPanel.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TextField from '@/components/shared/TextField.vue';
import AvailableDiscounts from '@/components/orders/modals/form/discount/AvailableDiscounts.vue';
import Toast from '@/utils/toast';

const namespace: string = 'orders';

@Component<MembershipInformation>({
	components: {
		TextField,
		ExpansionPanel,
		TextButton,
		ValidationObserver,
		ValidationProvider,
		AvailableDiscounts
	}
})
export default class MembershipInformation extends Vue {
	@Action('lookupMembership', { namespace }) private lookupMembership!: ({ identifier } : { identifier: string }) => Promise<MembershipLookupResult | undefined>;
	@Action('applyPromoCode', { namespace }) private applyPromoCode!: ({ memberInfo, menus } : { memberInfo: CheckoutMemberInfo; menus: Menu[] }) => Promise<void>;
	@Getter('getMembershipLookup', { namespace }) private lookupResult!: MembershipLookupResult | null;
	@Getter('getModifyingOrder', { namespace }) private modifyingOrder!: boolean;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Prop({ type: Array, required: true }) private menus!: Menu[];
	@Prop({ type: Object, required: true }) private membershipCustomization!: MembershipCustomization;
	@Prop({ type: Object, required: true }) private checkout!: Checkout;

	$refs!: {
		observer: Validate;
		provider: Provider;
	};

	private identifier: string = '';
	private successMsg: string = '';
	private loading: boolean = false;
	private membershipApplied: boolean = false;
	private showAvailableDiscountsModal: boolean = false;
	private applyingDiscount: boolean = false;

	/**
	 * Get the input settings based on the input type
	 *
	 * @return {MembershipInputSettings}
	 */
	private get inputSettings(): MembershipInputSettings {
		return getMembershipInputSettings(this.membershipCustomization);
	}

	/**
	 * IDs of the promos already applied to the order, used to pre-check the modal.
	 * Read from memberInfo.promos (set for every provider) rather than promosApplied,
	 * which item-level providers like ELB leave empty.
	 *
	 * @return {string[]}
	 */
	private get appliedPromoIds(): string[] {
		return this.checkout.memberInfo?.promos ? this.checkout.memberInfo.promos.map((promo: MemberPromo | PunchhPromo) => String(promo.id)) : [];
	}

	/**
	 * Provider the picker is restricted to. Adding to an open check only allows Punchh
	 * (others apply at check open), so it defaults to Punchh when nothing's applied.
	 * Modifying locks to the applied provider. A new check stays unlocked so staff can
	 * switch providers freely before placing.
	 *
	 * @return {string}
	 */
	private get lockedProvider(): string {
		const provider = this.checkout.memberInfo?.provider || '';
		if (this.orderDetails.checkNum) {
			return provider || MEMBERSHIP_PROVIDERS.PUNCHH;
		}
		if (this.modifyingOrder) {
			return provider;
		}
		return '';
	}

	/**
	 * View-only when adding to an open check with a non-Punchh discount applied —
	 * those can't be changed once the check is open.
	 *
	 * @return {boolean}
	 */
	private get readOnlyDiscounts(): boolean {
		return !!this.orderDetails.checkNum && this.lockedProvider !== MEMBERSHIP_PROVIDERS.PUNCHH;
	}

	private created(): void {
		// Check if membership has already been applied
		if (this.checkout.memberInfo?.identifier) {
			if(this.inputSettings.type === 'tel') {
				this.identifier = this.checkout.memberInfo.identifier.match(/\d{3}(?=\d{2,3})|\d+/g)!.join('-');
			}
			else {
				this.identifier = this.checkout.memberInfo.identifier;
			}
			this.membershipApplied = true;
			this.successMsg = this.$t('orders.guest_details.membership.membership_applied');
		}
	}

	/**
	 * Format the phone number - remove non-digital characters
	 * and add dashes to the respective positions
	 *
	 * @return {void}
	 */
	private formatInput(): void {
		if(this.inputSettings.type === 'tel' && this.identifier) {
			this.identifier = formatPhoneNumber(this.identifier);
		}
	}

	/**
	 * Look up the member across the location's providers. On success, lock the
	 * identifier and let staff pick a provider; auto-select when there's only one.
	 *
	 * @return {Promise<void>}
	 */
	private async applyMembership(): Promise<void> {
		try {
			let isValid = await this.$refs.observer.validate();
			this.loading = true;
			if (isValid) {
				const tempIdentifier = this.inputSettings.type === 'tel' ? this.identifier.replace(/[^0-9]/g, '') : this.identifier;
				const result = await this.lookupMembership({ identifier: tempIdentifier });
				if (!result?.member?.id || !result.providers.length) {
					throw new Error('membership-not-found');
				}
				this.$refs.provider.setErrors([]);
				this.membershipApplied = true;
			}
		}
		catch (error) {
			this.successMsg = '';
			this.membershipApplied = false;
			this.$refs.provider.setErrors([this.$t('orders.guest_details.membership.membership_not_applied', { type: this.inputSettings.label.toLowerCase() })]);
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Apply the staff's discount selection: map the chosen provider's promos into the
	 * member info and recalculate costs.
	 *
	 * @param {{ provider: string; promos: (MemberPromo | PunchhPromo)[] }} payload
	 * @return {Promise<void>}
	 */
	private async onApplyDiscounts(payload: { provider: string; promos: (MemberPromo | PunchhPromo)[] }): Promise<void> {
		const providerName = payload.provider || this.checkout.memberInfo?.provider;
		const group = this.lookupResult?.providers.find(providerGroup => providerGroup.provider === providerName);
		if (!this.lookupResult || !group) {
			return;
		}
		this.applyingDiscount = true;
		try {
			await this.applyPromoCode({ memberInfo: buildMemberInfoFromProvider(this.lookupResult, group, payload.promos), menus: this.menus });
			this.successMsg = this.$t('orders.guest_details.membership.membership_applied');
			this.showAvailableDiscountsModal = false;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.guest_details.discount.error_applying', { errorMessage }));
		}
		finally {
			this.applyingDiscount = false;
		}
	}
}
